import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWindowMinimize, faWindowMaximize, faWindowRestore, faVideo } from '@fortawesome/free-solid-svg-icons';
import '../../styles/meetings.css';

/**
 * A meeting in progress, as a window inside the portal.
 *
 * Opens at 80% of the screen, centred. Drag it by the title bar, resize it from
 * any edge or corner. Maximise (or double-click the title bar) fills the whole
 * screen, and Restore puts it back where it was. Minimise sends it to a small
 * pill (MeetingPill) and the meeting carries on - the same behaviour as the
 * chat call window (components/call/CallWindow.js), which this follows without
 * sharing any of its code.
 *
 * Deliberately not a modal - no backdrop, nothing blocked outside its own
 * rectangle - so somebody in a meeting can still look something up in the
 * portal. It lives above the page routes (context/MeetingContext.js), so
 * moving between pages never touches it.
 */

const MIN_W = 420;
const MIN_H = 380;
const OPEN_RATIO = 0.8;
const HANDLES = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const centredRect = () => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min(Math.max(Math.round(vw * OPEN_RATIO), MIN_W), vw);
    const h = Math.min(Math.max(Math.round(vh * OPEN_RATIO), MIN_H), vh);
    return { x: Math.round((vw - w) / 2), y: Math.round((vh - h) / 2), w, h };
};

/** Keep the whole window on screen - a title bar dragged off the edge cannot be grabbed back. */
const fitRect = (r) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min(r.w, vw);
    const h = Math.min(r.h, vh);
    return { w, h, x: clamp(r.x, 0, vw - w), y: clamp(r.y, 0, vh - h) };
};

const resizeBy = (dir) => (r, dx, dy) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let { x, y, w, h } = r;

    if (dir.includes('e')) w = clamp(r.w + dx, MIN_W, vw - r.x);
    if (dir.includes('s')) h = clamp(r.h + dy, MIN_H, vh - r.y);
    // The left and top edges move the origin, with the opposite edge pinned.
    if (dir.includes('w')) {
        w = clamp(r.w - dx, MIN_W, r.x + r.w);
        x = r.x + r.w - w;
    }
    if (dir.includes('n')) {
        h = clamp(r.h - dy, MIN_H, r.y + r.h);
        y = r.y + r.h - h;
    }
    return { x, y, w, h };
};

// On a phone there is no room for a window beside the page: it fills the screen.
const isSmallScreen = () => window.matchMedia?.('(max-width: 760px)').matches;

const MeetingWindow = ({ title, status, onMinimize, children }) => {
    const [rect, setRect] = useState(centredRect);
    const [maximized, setMaximized] = useState(isSmallScreen);
    const [gesture, setGesture] = useState(null); // 'drag' | 'resize' | null

    const rectRef = useRef(rect);
    rectRef.current = rect;
    const endGestureRef = useRef(null);

    // The browser window shrank - pull the meeting back inside it.
    useEffect(() => {
        const onResize = () => setRect((r) => fitRect(r));
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // The meeting can end mid-drag; don't leave listeners and a no-select body behind.
    useEffect(() => () => endGestureRef.current?.(), []);

    /** Follow the pointer until it is released. Listeners go on window: a fast drag outruns the title bar. */
    const track = (kind, event, apply) => {
        if (event.button !== 0) return;
        event.preventDefault();

        const start = { x: event.clientX, y: event.clientY, rect: rectRef.current };
        setGesture(kind);
        document.body.classList.add('mt-window-busy');

        const onMove = (ev) => setRect(apply(start.rect, ev.clientX - start.x, ev.clientY - start.y));
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
            document.body.classList.remove('mt-window-busy');
            endGestureRef.current = null;
            setGesture(null);
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
        endGestureRef.current = onUp;
    };

    const onBarPointerDown = (event) => {
        if (maximized || event.target.closest('button')) return;
        track('drag', event, (r, dx, dy) => fitRect({ ...r, x: r.x + dx, y: r.y + dy }));
    };

    const onBarDoubleClick = (event) => {
        if (event.target.closest('button')) return;
        setMaximized((m) => !m);
    };

    const style = maximized
        ? {} // sized by .mt-window.is-max in the stylesheet (dvh: iOS Safari's 100vh runs under its toolbar)
        : { left: rect.x, top: rect.y, width: rect.w, height: rect.h };

    const classes = ['mt-window', gesture && `is-${gesture}`, maximized && 'is-max'].filter(Boolean).join(' ');

    return (
        <div className={classes} style={style} role="dialog" aria-label={`Meeting: ${title}`}>
            <div className="mt-window-bar" onPointerDown={onBarPointerDown} onDoubleClick={onBarDoubleClick}>
                <span className="mt-window-title"><FontAwesomeIcon icon={faVideo} /> {title}</span>
                {status === 'reconnecting' && <span className="mt-window-status">Reconnecting…</span>}
                <span className="mt-window-buttons">
                    <button type="button" onClick={onMinimize} title="Minimise - the meeting keeps going" aria-label="Minimise">
                        <FontAwesomeIcon icon={faWindowMinimize} />
                    </button>
                    <button
                        type="button"
                        onClick={() => setMaximized((m) => !m)}
                        title={maximized ? 'Restore' : 'Maximise'}
                        aria-label={maximized ? 'Restore' : 'Maximise'}
                    >
                        <FontAwesomeIcon icon={maximized ? faWindowRestore : faWindowMaximize} />
                    </button>
                </span>
            </div>

            <div className="mt-window-body">{children}</div>

            {!maximized && HANDLES.map((dir) => (
                <div
                    key={dir}
                    className={`mt-window-handle is-${dir}`}
                    onPointerDown={(e) => track('resize', e, resizeBy(dir))}
                />
            ))}
        </div>
    );
};

export default MeetingWindow;
